import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Grid, TextField } from '@mui/material';
import axios from 'axios';
import {
  buildLoanReceivingProfiles,
  loanRecordToProfile,
  pickLoanRecordForReference
} from '../../utils/loanReceivingProfiles';

const LoanReceivingMetaFields = ({
  meta,
  onMetaChange,
  onProfileApply,
  lockMeta = false,
  disabled = false
}) => {
  const [profiles, setProfiles] = useState([]);
  const [loanInRecords, setLoanInRecords] = useState([]);
  const [loanOutRecords, setLoanOutRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const [receivingRes, issuanceRes] = await Promise.all([
        axios.get('/api/store/receiving', {
          params: { loanDirection: 'LOAN_IN', workflowStatus: 'APPROVED', loanBorrowOnly: 'true' }
        }),
        axios.get('/api/store/loan/issuances', { params: { loanDirection: 'LOAN_OUT' } })
      ]);
      const loanIn = Array.isArray(receivingRes.data) ? receivingRes.data : [];
      const loanOut = Array.isArray(issuanceRes.data) ? issuanceRes.data : [];
      setLoanInRecords(loanIn);
      setLoanOutRecords(loanOut);
      setProfiles(buildLoanReceivingProfiles(loanIn, loanOut).profiles);
    } catch {
      setLoanInRecords([]);
      setLoanOutRecords([]);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const counterparties = useMemo(
    () => [...new Set(profiles.map((p) => p.counterpartyCompany).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [profiles]
  );

  const loanRefOptions = useMemo(() => {
    const company = String(meta.counterpartyCompany || '').trim();
    const refs = profiles
      .filter((p) => !company || p.counterpartyCompany === company)
      .map((p) => p.loanReferenceNumber)
      .filter(Boolean);
    return [...new Set(refs)].sort((a, b) => a.localeCompare(b));
  }, [profiles, meta.counterpartyCompany]);

  const resolveRecord = (profile) => {
    if (!profile?.recordId) return null;
    if (profile.type === 'LOAN_IN') {
      return loanInRecords.find((r) => String(r._id) === profile.recordId) || null;
    }
    return loanOutRecords.find((r) => String(r._id) === profile.recordId) || null;
  };

  const applyProfile = (profile) => {
    if (!profile) return;
    onMetaChange({
      counterpartyCompany: profile.counterpartyCompany,
      counterpartyContactName: profile.counterpartyContactName,
      counterpartyContactPhone: profile.counterpartyContactPhone,
      loanReferenceNumber: profile.loanReferenceNumber,
      returnDueDate: profile.returnDueDate || meta.returnDueDate
    });
    const record = resolveRecord(profile);
    if (record && onProfileApply) {
      onProfileApply(profile, record);
    }
  };

  const matchProfile = () => {
    const company = String(meta.counterpartyCompany || '').trim();
    const ref = String(meta.loanReferenceNumber || '').trim();
    if (!company && !ref) return null;
    return (
      profiles.find((p) => p.counterpartyCompany === company && p.loanReferenceNumber === ref)
      || profiles.find((p) => p.loanReferenceNumber && p.loanReferenceNumber === ref)
      || profiles.find((p) => p.counterpartyCompany === company)
      || null
    );
  };

  const handleRecipientChange = (_, value) => {
    const next = String(value || '').trim();
    onMetaChange({ counterpartyCompany: next });
    const hit = profiles.find((p) => p.counterpartyCompany === next);
    if (hit && hit.loanReferenceNumber) applyProfile(hit);
  };

  const applyLoanReference = (loanRef) => {
    const ref = String(loanRef || '').trim();
    if (!ref) return;
    const company = String(meta.counterpartyCompany || '').trim();
    const picked = pickLoanRecordForReference(
      loanInRecords,
      loanOutRecords,
      ref,
      company,
      meta.loanReceivingPurpose || 'LOAN_IN'
    );
    if (picked) {
      const profile = loanRecordToProfile(picked.record, picked.type);
      if (profile) {
        applyProfile(profile);
        return;
      }
    }
    const hit =
      profiles.find((p) => p.loanReferenceNumber === ref && (!company || p.counterpartyCompany === company))
      || profiles.find((p) => p.loanReferenceNumber === ref);
    if (hit) applyProfile(hit);
  };

  const handleLoanRefChange = (_, value) => {
    const next = String(value || '').trim();
    onMetaChange({ loanReferenceNumber: next });
    if (next) applyLoanReference(next);
  };

  const handleLoanRefBlur = () => {
    const ref = String(meta.loanReferenceNumber || '').trim();
    if (ref) applyLoanReference(ref);
    else {
      const hit = matchProfile();
      if (hit) applyProfile(hit);
    }
  };

  const fieldsDisabled = disabled || lockMeta;

  return (
    <>
      <Grid item xs={12} md={6}>
        <Autocomplete
          freeSolo
          options={counterparties}
          value={meta.counterpartyCompany || ''}
          onChange={handleRecipientChange}
          onInputChange={(_, value, reason) => {
            if (reason === 'input') onMetaChange({ counterpartyCompany: value });
          }}
          disabled={fieldsDisabled}
          loading={loading}
          renderInput={(params) => (
            <TextField {...params} required fullWidth label="Recipient Company" />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          freeSolo
          options={loanRefOptions}
          value={meta.loanReferenceNumber || ''}
          onChange={handleLoanRefChange}
          onInputChange={(_, value, reason) => {
            if (reason === 'input') onMetaChange({ loanReferenceNumber: value });
          }}
          onBlur={handleLoanRefBlur}
          disabled={fieldsDisabled}
          loading={loading}
          renderInput={(params) => (
            <TextField {...params} fullWidth label="Loan Reference Number" />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          type="date"
          label="Return Due Date"
          value={meta.returnDueDate || ''}
          onChange={(e) => onMetaChange({ returnDueDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          disabled={fieldsDisabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Recipient Contact Name"
          value={meta.counterpartyContactName || ''}
          onChange={(e) => onMetaChange({ counterpartyContactName: e.target.value })}
          disabled={fieldsDisabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Recipient Contact Phone"
          value={meta.counterpartyContactPhone || ''}
          onChange={(e) => onMetaChange({ counterpartyContactPhone: e.target.value })}
          disabled={fieldsDisabled}
        />
      </Grid>
    </>
  );
};

export default LoanReceivingMetaFields;
